/**
 * AspectCropUpload — reusable image uploader with a mandatory crop step.
 *
 * The admin picks a file, is forced into a `react-easy-crop` modal with the
 * requested aspect ratio, and the cropped region is exported as a JPEG at its
 * *original* pixel resolution (we keep the full detail the user picked and
 * only enforce the aspect ratio, per product spec). The exported blob is
 * posted to `/admin/upload` and the returned URL is handed back via onChange.
 *
 * Props:
 *   value         — current URL ("" when empty)
 *   onChange(url) — called after upload or after Kaldır
 *   aspect        — e.g. 1200/630 (cover) or 1080/1920 (story)
 *   aspectLabel   — human-readable aspect label shown in modal, e.g. "1200×630"
 *   label         — Field label
 *   hint          — Field hint
 *   testid        — test-id prefix
 *   previewClass  — tailwind classes for the thumbnail box (shape/size)
 *   accent        — tailwind color token used for hover accents (e.g. "rose")
 */
import { useRef, useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Loader2, Upload, X, Crop as CropIcon } from "lucide-react";
import { api, img, errMsg } from "../../lib/api";
import { Field } from "./Fields";
import { Button } from "../ui/button";

async function cropToBlob(imageSrc, pixels, mimeType = "image/jpeg", quality = 0.92) {
  const image = await new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  // Keep the original pixel resolution of the cropped region (we only enforce
  // aspect ratio, not a fixed output size, so quality is preserved).
  canvas.width = Math.round(pixels.width);
  canvas.height = Math.round(pixels.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, pixels.x, pixels.y, pixels.width, pixels.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), mimeType, quality));
}

export default function AspectCropUpload({
  value,
  onChange,
  aspect,
  aspectLabel = "",
  label = "Görsel",
  hint = "",
  testid = "aspect-upload",
  previewClass = "w-full h-32 rounded-lg",
  accent = "rose",
  required = false,
}) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);

  const pickFile = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("JPEG, PNG veya WebP seçin");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Dosya 10 MB'den büyük olamaz");
      return;
    }
    const url = URL.createObjectURL(f);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPx(null);
    setOpen(true);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_, px) => setAreaPx(px), []);

  const saveCrop = async () => {
    if (!src || !areaPx) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, areaPx, "image/jpeg", 0.92);
      if (!blob) throw new Error("Kırpma başarısız");
      const file = new File([blob], "crop.jpg", { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange?.(data.url);
      toast.success("Görsel kırpıldı ve yüklendi");
      setOpen(false);
      URL.revokeObjectURL(src);
      setSrc("");
    } catch (e) {
      toast.error(errMsg(e) || "Yüklenemedi");
    }
    setBusy(false);
  };

  const closeModal = () => {
    if (busy) return;
    if (src) URL.revokeObjectURL(src);
    setSrc("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    toast.info("Görsel kaldırıldı");
  };

  return (
    <Field label={label + (required ? " *" : "")} hint={hint}>
      <div data-testid={testid}>
        {value ? (
          <div className="relative rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
            <img src={img(value)} alt="" className={`${previewClass} object-cover block`} data-testid={`${testid}-preview`} />
            <div className="absolute top-1 right-1 flex items-center gap-1">
              <button
                type="button"
                onClick={pickFile}
                className="w-7 h-7 bg-white/95 hover:bg-white rounded-full flex items-center justify-center text-stone-700 shadow border border-stone-200"
                title="Değiştir"
                data-testid={`${testid}-replace`}
              >
                <Upload size={12} />
              </button>
              <button
                type="button"
                onClick={clear}
                className="w-7 h-7 bg-white/95 hover:bg-white rounded-full flex items-center justify-center text-red-600 shadow border border-stone-200"
                title="Kaldır"
                data-testid={`${testid}-clear`}
              >
                <X size={12} />
              </button>
            </div>
            {aspectLabel && (
              <div className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white rounded px-1.5 py-0.5 font-medium tabular-nums">
                {aspectLabel}
              </div>
            )}
          </div>
        ) : (
          <label
            className={`${previewClass} border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center text-stone-400 hover:border-${accent}-400 hover:text-${accent}-600 cursor-pointer transition`}
            data-testid={`${testid}-dropzone`}
          >
            <Upload size={18} />
            <span className="text-[11px] mt-1 font-medium">Görsel yükle</span>
            {aspectLabel && <span className="text-[10px] mt-0.5 opacity-70">{aspectLabel} oranına kırpılacak</span>}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFile}
              data-testid={`${testid}-input`}
            />
          </label>
        )}
        {/* Hidden picker for replace button when value exists */}
        {value && (
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onFile}
          />
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
          data-testid={`${testid}-modal`}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-stone-50">
              <div className="flex items-center gap-2">
                <CropIcon size={15} className="text-stone-500" />
                <p className="font-semibold text-sm">Görseli kırp {aspectLabel ? `· ${aspectLabel}` : ""}</p>
              </div>
              <button type="button" onClick={closeModal} className="text-stone-500 hover:text-stone-900 p-1" data-testid={`${testid}-modal-close`}>
                <X size={16} />
              </button>
            </div>
            <div className="relative bg-stone-900 h-[420px]">
              {src && (
                <Cropper
                  image={src}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  cropShape="rect"
                  showGrid
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <div className="px-5 py-4 space-y-3 border-t">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-stone-500 w-14 shrink-0">Yakınlaş</span>
                <input
                  type="range"
                  min={1}
                  max={4}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1"
                  data-testid={`${testid}-modal-zoom`}
                />
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <p className="text-[11px] text-stone-500">
                  Kırpma oranı sabittir. Orijinal çözünürlük korunur.
                </p>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" onClick={closeModal} disabled={busy} data-testid={`${testid}-modal-cancel`}>Vazgeç</Button>
                  <Button type="button" onClick={saveCrop} disabled={busy || !areaPx} data-testid={`${testid}-modal-save`}>
                    {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CropIcon size={14} className="mr-1" />}
                    Kırp ve Kaydet
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Field>
  );
}
